import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import escapeStringRegexp from 'escape-string-regexp';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, switchMap, takeUntil, tap } from 'rxjs/operators';

import { List, ListItem } from '@site-mate/dashpivot-shared-library';

import { ListItemsService } from 'app/lists/list-items.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';

@Component({
  selector: 'cc-list-typeahead',
  templateUrl: './list-typeahead.component.html',
  styleUrls: ['./list-typeahead.component.scss'],
})
export class ListTypeaheadComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  @Input() list: List;
  @Input() parentId: string;
  @Input() resultsLimit = 10;
  @Output() readonly listDeployed = new EventEmitter<List>();
  @Output() readonly cancel = new EventEmitter<void>();
  listItems$ = new BehaviorSubject([] as ListItem[]);
  readonly search$ = new BehaviorSubject<string>('');

  isLoading = false;
  emptyRecord = { id: null, value: '' } as ListItem;

  debounceInputTime = 500;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly errorHandler: ErrorHandler,
    private readonly listItemsService: ListItemsService,
  ) {}

  ngOnInit() {
    const limit = String(this.resultsLimit);

    this.search$
      .pipe(
        takeUntil(this.destroy$),
        tap(this.setLoadingState.bind(this)),
        debounceTime(this.debounceInputTime),
        filter((query) => query !== null),
        switchMap((query) => this.queryListItems(query, limit)),
        catchError(() => of([])),
      )
      .subscribe(this.updateListItems.bind(this));
  }

  private setLoadingState() {
    this.isLoading = true;
    this.listItems$.next([]);
  }

  private queryListItems(query: string, limit: string) {
    this.changeDetectorRef.markForCheck();
    return this.search$.value.trim().length ? this.getListItems(query, limit) : of([]);
  }

  private getListItems(query: string, limit: string) {
    return this.listItemsService.getListItems(this.list.id, {
      query,
      limit,
      deployedParentsExclude: this.parentId,
    });
  }

  private updateListItems(listItems: ListItem[]) {
    this.isLoading = false;
    const listItemsWithInsertOption = this.getListItemsWithInsertOption(listItems);
    this.listItems$.next(listItemsWithInsertOption);
    this.changeDetectorRef.markForCheck();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  highlightMatches(query: string | undefined, value: string | null, target: string) {
    const escapedString = escapeStringRegexp(query || '');
    const localQuery = new RegExp(`(${escapedString})`, 'gi');
    return value ? target.replace(localQuery, '<strong>$1</strong>') : `Add "<strong>${target}</strong>"`;
  }

  onChange(value: string) {
    this.listItemsService
      .deploy(this.list.id, { value, projectId: this.parentId })
      .pipe(
        catchError((error) => {
          this.errorHandler.handle(error);
          return of(null);
        }),
      )
      .subscribe(({ fullList }) => {
        this.listDeployed.emit(fullList);
      });
  }

  private getListItemsWithInsertOption(listItems: ListItem[]): ListItem[] {
    const combinedListItems = [...listItems, ...this.list.items];
    const { value = '' } = this.search$;

    if (this.hasNoMatchingEntry(combinedListItems, value)) {
      const emptyStateItem = { ...this.emptyRecord, value };
      return [emptyStateItem, ...listItems];
    }

    return listItems;
  }

  private hasNoMatchingEntry(listItems: ListItem[], searchCriteria: string): boolean {
    const valueMatcher = new RegExp(`^${escapeStringRegexp(searchCriteria)}$`, 'i');

    return searchCriteria.trim().length && !listItems.some((listItem) => valueMatcher.test(listItem.value));
  }
}
